import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2, ArrowUpDown } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAppSelector } from "@/hooks/reducer-hooks";
import { Building, Promotion, Resident } from "@/constants/objects";
import {
  getResidentsInBuildingsByPromotionLogicId,
  removeResident,
} from "@/services/userDataService";
import { ResidentTableSkeleton } from "@/components/skeletons/ResidentTableSkeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AddResidentDialog } from "@/components/AddResidentDialog";
import { ResidentFilters } from "@/components/ResidentFilters";

type SortConfig = {
  key: keyof Resident | "promotionName";
  direction: "asc" | "desc";
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function ResidentTable() {
  const user = useAppSelector((state) => state.user);
  const [residents, setResidents] = useState([] as Resident[]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const promotions: Promotion[] = user.subscriptionPromotions || [];
  const [filters, setFilters] = useState({
    search: "",
    buildingId: "",
    promotionId: "",
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: "email",
    direction: "asc",
  });

  // Reset to first page when changing items per page
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  // Derive buildings from residents data using useMemo
  const buildings: Building[] = useMemo(() => {
    if (!residents || residents.length === 0) {
      return [];
    }

    // Use Map to ensure uniqueness by building ID
    const uniqueBuildings = new Map();

    residents
      .filter((resident) => resident && resident.buildingId && resident.buildingTitle)
      .forEach((resident) => {
        if (!uniqueBuildings.has(resident.buildingId)) {
          uniqueBuildings.set(resident.buildingId, {
            _id: resident.buildingId,
            title: resident.buildingTitle,
          });
        }
      });

    return Array.from(uniqueBuildings.values()).sort((a, b) =>
      (a.title || "").localeCompare(b.title || "")
    );
  }, [residents]);

  // Fetch data once the component mounts
  useEffect(() => {
    const fetchResidents = async () => {
      try {
        const residents = await getResidentsInBuildingsByPromotionLogicId(
          promotions.map((promotion) => promotion.id)
        );

        setResidents(residents);
      } catch (error) {
        toast({
          title: "Failed to fetch residents",
          description: "Please try again.",
          variant: "destructive",
        });
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchResidents();
  }, []);

  const handleSort = (key: SortConfig["key"]) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortedResidents = (residents: Resident[]) => {
    return [...residents].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Special handling for promotion name
      if (sortConfig.key === "promotionName") {
        aValue = promotions.find((p) => p.id === a.subscriptionPromotionLogicId)?.name || "";
        bValue = promotions.find((p) => p.id === b.subscriptionPromotionLogicId)?.name || "";
      }

      if (aValue === null) return 1;
      if (bValue === null) return -1;

      const comparison = String(aValue).localeCompare(String(bValue));
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  };

  const filteredResidents: Resident[] = residents.filter((resident) => {
    const matchesEmail = resident.email.toLowerCase().includes(filters.search.toLowerCase());
    const matchesBuilding = !filters.buildingId || resident.buildingId === filters.buildingId;
    const matchesPromotion =
      !filters.promotionId || resident.subscriptionPromotionLogicId === filters.promotionId;

    return matchesEmail && matchesBuilding && matchesPromotion;
  });

  const sortedResidents = getSortedResidents(filteredResidents);

  const pageCount: number = Math.ceil(sortedResidents.length / itemsPerPage);
  const paginatedResidents: Resident[] = sortedResidents.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleDelete = (resident: Resident) => {
    setResidents(residents.filter((r) => r.email !== resident.email));

    removeResident(resident)
      .then(() => {
        toast({
          title: "Resident deleted",
          description: "The resident has been successfully deleted.",
          variant: "success",
        });
      })
      .catch((error) => {
        console.error("Error deleting resident:", error);
        toast({
          title: "Failed to delete resident",
          description: "Please try again.",
          variant: "destructive",
        });
        setResidents([...residents]);
      });
  };
  if (loading) {
    return <ResidentTableSkeleton />;
  }

  return (
    <div>
      <div className="flex gap-4 w-full mb-4">
        <div className="flex-1">
          <ResidentFilters
            buildings={buildings}
            promotions={promotions}
            filters={filters}
            onChange={setFilters}
          />
        </div>
        <AddResidentDialog promotionsList={promotions} />
      </div>

      <div className="border rounded-md">
        <div className="overflow-auto max-h-[600px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>
                  <Button
                    variant="ghost"
                    onClick={() => handleSort("email")}
                    className="flex items-center gap-1"
                  >
                    Email
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    onClick={() => handleSort("buildingTitle")}
                    className="flex items-center gap-1"
                  >
                    Building
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead>
                  <Button
                    variant="ghost"
                    onClick={() => handleSort("promotionName")}
                    className="flex items-center gap-1"
                  >
                    Promotion
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedResidents.map((resident) => (
                <TableRow key={resident.email}>
                  <TableCell className="text-left">{resident.email}</TableCell>
                  <TableCell className="text-left">{resident.buildingTitle}</TableCell>
                  <TableCell className="text-left">
                    {promotions.find(
                      (promotion) => promotion.id === resident.subscriptionPromotionLogicId
                    )?.name || "Unknown"}
                  </TableCell>
                  <TableCell className="text-left">
                    {!promotions.find(
                      (promotion) => promotion.id === resident.subscriptionPromotionLogicId
                    )?.isVoucher && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. This will permanently remove the
                              resident from the list.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(resident)}>
                              Continue
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <div className="flex justify-between items-center mt-6">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Rows per page:</span>
          <Select
            value={itemsPerPage.toString()}
            onValueChange={(value) => setItemsPerPage(Number(value))}
          >
            <SelectTrigger className="w-[80px]">
              <SelectValue placeholder="10" />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Pagination className="w-full max-w-md">
          <PaginationContent>
            {/* Previous Button */}
            <PaginationItem>
              <PaginationPrevious
                href={"#"}
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage > 1) setCurrentPage(currentPage - 1);
                }}
              />
            </PaginationItem>

            {/* Page Number Links */}
            {Array.from({ length: pageCount }).map((_, index) => {
              const pageNum = index + 1;
              if (
                pageNum === 1 ||
                pageNum === pageCount ||
                (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
              ) {
                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      isActive={pageNum === currentPage}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentPage(pageNum);
                      }}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                return <PaginationEllipsis key={pageNum} />;
              }
            })}

            {/* Next Button */}
            <PaginationItem>
              <PaginationNext
                href={"#"}
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage < pageCount) setCurrentPage(currentPage + 1);
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
      <div className="text-sm text-muted-foreground mb-2">
        Showing {filteredResidents.length} results out of {residents.length} residents
      </div>
    </div>
  );
}
